// Post-invite premium modal — shows the freshly generated code, a scannable
// QR-code with the join-URL, plus copy + share buttons.
// Replaces the old "clipboard-only" toast flow on Employees.jsx.
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, X, Share2, QrCode } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nContext";

export default function InviteCodeModal({ open, code, role, joinUrl, onClose }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  useEffect(() => {
    if (!open) { setCopied(false); setCopiedUrl(false); }
  }, [open]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t("error"));
    }
  };
  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 1800);
    } catch {
      toast.error(t("error"));
    }
  };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: t("invite_share_title"),
          text: t("invite_share_text").replace("{code}", code),
          url: joinUrl,
        });
      } catch { /* user cancelled */ }
    } else {
      copyUrl();
    }
  };

  return (
    <AnimatePresence>
      {open && code && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-md grid place-items-center p-4"
          onClick={onClose}
          data-testid="invite-modal"
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            className="card w-full max-w-md p-0 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "var(--shadow-xl)" }}
          >
            <div className="flex items-start justify-between p-6 pb-4">
              <div>
                <div className="eyebrow">{t("invite_modal_eyebrow")}</div>
                <h3 className="font-display text-[22px] font-bold tracking-tight mt-0.5">
                  {t("invite_modal_title")}
                </h3>
                {role && (
                  <p className="text-[12.5px] text-[hsl(var(--text-muted))] mt-1">
                    {t("invite_modal_role")}: <span className="font-semibold text-[hsl(var(--text-strong))]">{t(`role_${role}`)}</span>
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 grid place-items-center rounded-full hover:bg-[hsl(var(--muted))] transition-colors"
                data-testid="invite-modal-close"
              >
                <X size={16} />
              </button>
            </div>

            {/* QR code */}
            <div className="mx-6 rounded-2xl bg-[hsl(var(--muted))] p-5 flex flex-col items-center gap-3" data-testid="invite-qr-wrap">
              <div className="p-3 rounded-xl bg-white shadow-[var(--shadow-xs)]">
                <QRCodeSVG
                  value={joinUrl}
                  size={168}
                  level="M"
                  bgColor="#ffffff"
                  fgColor="#0033A0"
                  data-testid="invite-qr"
                />
              </div>
              <div className="text-center">
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--primary))] flex items-center justify-center gap-1.5">
                  <QrCode size={11} strokeWidth={2.25} />
                  {t("invite_scan_hint")}
                </div>
                <div className="text-[12px] text-[hsl(var(--text-muted))] mt-1 max-w-[240px] mx-auto leading-snug">
                  {t("invite_scan_desc")}
                </div>
              </div>
            </div>

            {/* Code block */}
            <div className="m-6 mt-4">
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--text-muted))] mb-2">
                {t("invite_code_label")}
              </div>
              <button
                onClick={copyCode}
                data-testid="invite-copy-code"
                className="w-full flex items-center justify-between gap-3 p-3.5 rounded-2xl bg-[hsl(var(--surface))] border hairline hover:border-[hsl(var(--primary))] transition-all group"
              >
                <span className="font-mono font-bold text-[22px] tabular-nums tracking-[0.15em] text-[hsl(var(--text-strong))]">
                  {code}
                </span>
                <span className={`w-9 h-9 shrink-0 grid place-items-center rounded-full transition-colors ${
                  copied ? "bg-[hsl(var(--success)_/_0.15)] text-[hsl(var(--success))]" : "bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))] group-hover:bg-[hsl(var(--primary-soft))] group-hover:text-[hsl(var(--primary))]"
                }`}>
                  {copied ? <Check size={16} strokeWidth={2.5} /> : <Copy size={14} strokeWidth={2} />}
                </span>
              </button>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 px-6 pb-6">
              <button
                onClick={copyUrl}
                data-testid="invite-copy-url"
                className="btn btn-quiet flex-1"
              >
                {copiedUrl ? <Check size={14} strokeWidth={2.5} /> : <Copy size={14} strokeWidth={2} />}
                {copiedUrl ? t("copied") : t("invite_copy_url")}
              </button>
              <button
                onClick={share}
                data-testid="invite-share"
                className="btn btn-primary flex-1"
              >
                <Share2 size={14} strokeWidth={2} />
                {t("invite_share")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
